import i18n from 'i18n-js';

import { AppThunkAction, AppThunkActionAsync } from 'store';

import { TableMaps } from 'api/controllers/tableMaps';
import { EditorTable, EntrancePosition } from 'components/tableMaps/CreateTableMapWizard/context';
import {
    editorTableToMapTableModel,
    entranceToMapEntrance,
    tablesMapDtoToDetails,
    tablesMapDtoToListItem,
    MapTableDTO,
    TablesMapDTO,
} from './mappers';
import { TableMapsAction } from './actions';
import globalStore from '../globalStore';

export class QrConflictError extends Error {
    readonly qrConflictLabels: string[];
    constructor(labels: string[]) {
        super(`QR_CONFLICT:${labels.join(',')}`);
        this.name = 'QrConflictError';
        this.qrConflictLabels = labels;
    }
}

/** The axios interceptor rejects with the server message itself, so errors arrive as strings. */
export function errorMessage(err: unknown): string | null {
    if (typeof err === 'string') return err;
    const data = (err as { response?: { data?: { Message?: unknown; message?: unknown } } })
        ?.response?.data;
    const msg = data?.Message ?? data?.message ?? (err as { message?: unknown })?.message;
    return typeof msg === 'string' ? msg : null;
}

function isQrConflictError(err: unknown): boolean {
    const msg = errorMessage(err);
    return typeof msg === 'string' && msg.toLowerCase().includes('qr code already assigned');
}

interface CreateTableMapParams {
    name: string;
    isActive: boolean;
    entrance: EntrancePosition | null;
    tables: EditorTable[];
    gridEnabled: boolean;
}

interface UpdateTableMapParams {
    id: string;
    name: string;
    isActive: boolean;
    entrance: EntrancePosition | null;
    tables: EditorTable[];
    gridEnabled: boolean;
    originalTables: EditorTable[];
}

function tableChanged(current: EditorTable, original: EditorTable): boolean {
    return (
        current.label !== original.label ||
        current.seats !== original.seats ||
        current.shape !== original.shape ||
        current.x !== original.x ||
        current.y !== original.y ||
        current.rotation !== original.rotation ||
        current.qrCode !== original.qrCode ||
        current.isActive !== original.isActive
    );
}

function buildTablesMapDto(mapDto: TablesMapDTO, tableDtos: MapTableDTO[]): TablesMapDTO {
    return { ...mapDto, tables: tableDtos };
}

export const actionCreators = {
    getTableMaps: (): AppThunkAction<TableMapsAction> => (dispatch) => {
        dispatch({ type: 'TABLE_MAPS_GET_START' });
        TableMaps.getTablesMaps()
            .then((dtos) => {
                const list = dtos.map(tablesMapDtoToListItem);
                dispatch({ type: 'TABLE_MAPS_GET_SUCCESS', list });
            })
            .catch(() => dispatch({ type: 'TABLE_MAPS_GET_ERROR' }));
    },

    deleteTableMap:
        (id: string): AppThunkAction<TableMapsAction> =>
        (dispatch) => {
            TableMaps.deleteTablesMap(id)
                .then(() => dispatch({ type: 'TABLE_MAP_DELETE', id }))
                .catch((e) =>
                    globalStore.actionCreators.showToaster(
                        'error',
                        errorMessage(e) ?? i18n.t('common.somethingWrong')
                    )(dispatch)
                );
        },

    setTableMapActive:
        (id: string, isActive: boolean): AppThunkActionAsync<TableMapsAction> =>
        async (dispatch) => {
            try {
                const current = await TableMaps.getTablesMap(id);
                await TableMaps.updateTablesMap(id, {
                    name: current.name,
                    isActive,
                    entrance: current.entrance,
                });

                const updatedDto = await TableMaps.getTablesMap(id);
                dispatch({
                    type: 'TABLE_MAP_UPDATE',
                    item: tablesMapDtoToListItem(updatedDto),
                    details: tablesMapDtoToDetails(updatedDto),
                });
            } catch (e) {
                globalStore.actionCreators.showToaster(
                    'error',
                    errorMessage(e) ?? i18n.t('common.somethingWrong')
                )(dispatch);
            }
        },

    createTableMap:
        (params: CreateTableMapParams): AppThunkActionAsync<TableMapsAction, string> =>
        async (dispatch) => {
            const mapDto = await TableMaps.createTablesMap({
                name: params.name,
                isActive: params.isActive,
                entrance: entranceToMapEntrance(params.entrance),
            });

            const tableResults = await Promise.allSettled(
                params.tables.map((t) =>
                    TableMaps.createMapTable(mapDto.id, editorTableToMapTableModel(t))
                )
            );

            const qrConflictLabels: string[] = [];
            const otherErrors: unknown[] = [];
            params.tables.forEach((t, i) => {
                const result = tableResults[i];
                if (result.status === 'rejected') {
                    if (isQrConflictError(result.reason)) {
                        qrConflictLabels.push(t.label);
                    } else {
                        otherErrors.push(result.reason);
                    }
                }
            });
            if (qrConflictLabels.length > 0) {
                throw new QrConflictError(qrConflictLabels);
            }
            if (otherErrors.length > 0) {
                throw otherErrors[0];
            }

            const tableDtos = tableResults.map(
                (r) => (r as PromiseFulfilledResult<MapTableDTO>).value
            );

            const fullDto = buildTablesMapDto(mapDto, tableDtos);
            const item = tablesMapDtoToListItem(fullDto);
            const details = tablesMapDtoToDetails(fullDto);
            details.gridEnabled = params.gridEnabled;

            dispatch({ type: 'TABLE_MAP_CREATE', item });
            dispatch({ type: 'TABLE_MAP_DETAILS_SET', details });

            return mapDto.id;
        },

    updateTableMap:
        (params: UpdateTableMapParams): AppThunkActionAsync<TableMapsAction> =>
        async (dispatch) => {
            await TableMaps.updateTablesMap(params.id, {
                name: params.name,
                isActive: params.isActive,
                entrance: entranceToMapEntrance(params.entrance),
            });

            const originalById = new Map(params.originalTables.map((t) => [t.id, t]));
            const currentIds = new Set(params.tables.map((t) => t.id));

            const deletedTables = params.originalTables.filter((t) => !currentIds.has(t.id));
            const deletePromises = deletedTables.map((t) =>
                TableMaps.deleteMapTable(params.id, t.id)
            );

            const newTables = params.tables.filter((t) => !originalById.has(t.id));

            const changedTables = params.tables.filter((t) => {
                const orig = originalById.get(t.id);
                return orig !== undefined && tableChanged(t, orig);
            });

            type TableOp = { table: EditorTable; fn: () => Promise<MapTableDTO> };
            const tableOps: TableOp[] = [
                ...newTables.map((t) => ({
                    table: t,
                    fn: () => TableMaps.createMapTable(params.id, editorTableToMapTableModel(t)),
                })),
                ...changedTables.map((t) => ({
                    table: t,
                    fn: () =>
                        TableMaps.updateMapTable(params.id, t.id, editorTableToMapTableModel(t)),
                })),
            ];

            const deleteResults = await Promise.allSettled(deletePromises);
            const tableResults = await Promise.allSettled(tableOps.map((op) => op.fn()));

            const qrConflictLabels: string[] = [];
            const otherErrors: unknown[] = [];
            tableResults.forEach((result, i) => {
                if (result.status === 'rejected') {
                    if (isQrConflictError(result.reason)) {
                        qrConflictLabels.push(tableOps[i].table.label);
                    } else {
                        otherErrors.push(result.reason);
                    }
                }
            });
            deleteResults.forEach((result) => {
                if (result.status === 'rejected') {
                    otherErrors.push(result.reason);
                }
            });
            if (qrConflictLabels.length > 0) {
                throw new QrConflictError(qrConflictLabels);
            }
            if (otherErrors.length > 0) {
                throw otherErrors[0];
            }

            const updatedDto = await TableMaps.getTablesMap(params.id);
            const item = tablesMapDtoToListItem(updatedDto);
            const details = tablesMapDtoToDetails(updatedDto);
            details.gridEnabled = params.gridEnabled;

            dispatch({ type: 'TABLE_MAP_UPDATE', item, details });
        },

    getTableMapDetails:
        (id: string): AppThunkActionAsync<TableMapsAction> =>
        async (dispatch) => {
            dispatch({ type: 'TABLE_MAP_DETAILS_LOADING', loading: true });
            try {
                const dto = await TableMaps.getTablesMap(id);
                const details = tablesMapDtoToDetails(dto);
                dispatch({ type: 'TABLE_MAP_DETAILS_SET', details });
            } catch {
                dispatch({ type: 'TABLE_MAP_DETAILS_LOADING', loading: false });
            }
        },
};
